import {Fragment} from 'react'

import {TourPopover, ProgressiveMask, StepControl, Hint} from '@/components/Tour'
import {useSkipTours} from '@/atoms/toursAtom'

const WalletNotConnectedHint = () => {
  const [skip, setSkip] = useSkipTours('walletNotConnectedHint')
  if (skip) return null
  return (
    <Hint name="walletNotConnectedHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} central>
          <p>Connect your Polkadot account first.</p>
          <p>Deposit at least 10 PHA in account.</p>
        </TourPopover>
      )}
    </Hint>
  )
}

const GenerateIdentityFirstHint = () => {
  const [skip, setSkip] = useSkipTours('generateIdentityFirstHint')
  if (skip) return null
  return (
    <Hint name="generateIdentityFirstHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} left={rect.left}>
          <p>
            Generate a unique identity with your on-chain and off-chain
            behaviors. The percentages represent the richness of your identity
            linking platform.
          </p>
        </TourPopover>
      )}
    </Hint>
  )
}

const RegisterSpiritHint = () => {
  const [skip, setSkip] = useSkipTours('registerSpiritHint')
  if (skip) return null
  return (
    <Hint name="registerSpiritHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} left={rect.left}>
          <p>
            Please ensure PhalaWorld and My Crypto Profile are logged in to the
            same account. Otherwise claim will fail.
          </p>
        </TourPopover>
      )}
    </Hint>
  )
}

const ActivateSpiritHint = () => {
  const [skip, setSkip] = useSkipTours('activateSpiritHint')
  if (skip) return null
  return (
    <Hint name="activateSpiritHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} central>
          <p>Starting claim spirit!</p>
        </TourPopover>
      )}
    </Hint>
  )
}

const UpdateSpiritDataHint = () => {
  const [skip, setSkip] = useSkipTours('updateSpiritDataHint')
  if (skip) return null
  return (
    <Hint name="updateSpiritDataHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} central>
          <p>Update the data from your identity.</p>
          <p>
            The specific actions data will be converted into four attributes in
            Spirit.
          </p>
          <p>
            Whenever you enrich actions data, you need to click the update
            button to update your attributes.
          </p>
        </TourPopover>
      )}
    </Hint>
  )
}

const ClaimSpiritHint = () => {
  const [skip, setSkip] = useSkipTours('claimSpiritHint')
  if (skip) return null
  return (
    <Hint name="claimSpiritHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} central>
          <p>Claim Spirit will mint a Spirit NFT to your wallet.</p>
        </TourPopover>
      )}
    </Hint>
  )
}

const LinkStartHint = () => {
  const [skip, setSkip] = useSkipTours('linkStartHint')
  if (skip) return null
  return (
    <Hint name="linkStartHint" onFinish={setSkip}>
      {(rect) => (
        <TourPopover top={rect.top + rect.height + 30} central>
          <p>
            Let&lsquo;s start the process to mint a Shell NFT that can be
            equipped to your Spirit NFT.
          </p>
        </TourPopover>
      )}
    </Hint>
  )
}

const ChooseRarityHint = () => {
  const [skip, setSkip] = useSkipTours('chooseRarityHint')
  if (skip) return null
  return (
    <Hint name="chooseRarityHint" onFinish={setSkip}>
      {(rect, dispatch) => (
        <TourPopover
          top={rect.top + rect.height * 0.8}
          central
          action={() => dispatch('cancel')}
          actionText="OK"
        >
          <p>
            Choose a rarity you want to mint. There are three rarities to choose
            from.
          </p>
        </TourPopover>
      )}
    </Hint>
  )
}

const LegendaryPreviewTour = () => {
  const [skip, setSkip] = useSkipTours('legendaryPreviewTour')
  if (skip) return null
  return (
    <ProgressiveMask
      name="legendaryPreview"
      onFinish={setSkip}
      steps={[
        'legendarySpecies',
        'legendaryZoomSpeciesDetail',
        'legendaryNavigateToPurchasePage',
      ]}
    >
      <StepControl name="legendaryPreview" when="legendarySpecies">
        {(rect, dispatch) => (
          <TourPopover
            top={rect.top - 80}
            left={rect.left + rect.width * 1.5 - 520}
            maxWidth={520}
            action={() => dispatch('next')}
            actionText="NEXT"
          >
            <p>There are four species can be previewed.</p>
          </TourPopover>
        )}
      </StepControl>
      <StepControl name="legendaryPreview" when="legendaryZoomSpeciesDetail">
        {(rect, dispatch) => (
          <TourPopover
            top={rect.top + rect.height - 15}
            left={rect.left}
            maxWidth={620}
            action={() => dispatch('next')}
            actionText="NEXT"
          >
            <p>
              Click on the spherical robot to zoom in on the details of each
              part.
            </p>
          </TourPopover>
        )}
      </StepControl>
      <StepControl
        name="legendaryPreview"
        when="legendaryNavigateToPurchasePage"
      >
        {(rect, dispatch) => (
          <TourPopover
            top={rect.top + rect.height + 30}
            left={rect.left}
            action={() => dispatch('next')}
            actionText="OK"
          >
            <p>Click to enter the purchase page.</p>
          </TourPopover>
        )}
      </StepControl>
    </ProgressiveMask>
  )
}

const MagicPreviewTour = () => {
  const [skip, setSkip] = useSkipTours('magicPreviewTour')
  if (skip) return null
  return (
    <ProgressiveMask
      name="magicPreview"
      onFinish={setSkip}
      steps={[
        'magicSpecies',
        'magicZoomSpeciesDetail',
        'magicNavigateToPurchasePage',
      ]}
    >
      <StepControl name="magicPreview" when="magicSpecies">
        {(rect, dispatch) => (
          <TourPopover
            top={rect.top - 80}
            left={rect.left}
            maxWidth={520}
            action={() => dispatch('next')}
            actionText="NEXT"
          >
            <p>There are four species can be previewed.</p>
          </TourPopover>
        )}
      </StepControl>
      <StepControl name="magicPreview" when="magicZoomSpeciesDetail">
        {(rect, dispatch) => (
          <TourPopover
            top={rect.top + rect.height - 15}
            left={rect.left}
            maxWidth={620}
            action={() => dispatch('next')}
            actionText="NEXT"
          >
            <p>
              Click on the spherical robot to zoom in on the details of each
              part.
            </p>
          </TourPopover>
        )}
      </StepControl>
      <StepControl name="magicPreview" when="magicNavigateToPurchasePage">
        {(rect, dispatch) => (
          <TourPopover
            top={rect.top + rect.height + 30}
            left={rect.left}
            action={() => dispatch('next')}
            actionText="OK"
          >
            <p>Click to enter the purchase page.</p>
          </TourPopover>
        )}
      </StepControl>
    </ProgressiveMask>
  )
}

const Tours = () => {
  return (
    <Fragment>
      <WalletNotConnectedHint />
      <UpdateSpiritDataHint />
      <RegisterSpiritHint />
      <GenerateIdentityFirstHint />
      <ActivateSpiritHint />
      <ClaimSpiritHint />
      <LinkStartHint />
      <ChooseRarityHint />
      <LegendaryPreviewTour />
      <MagicPreviewTour />
    </Fragment>
  )
}

export default Tours
